(() => {
  if (customElements.get('bagspot-size-tools')) return;
  const matchRanges = (rows, mode, first, second, third, tolerance = 0) => {
    const keys = mode === 'bra' ? ['underbust', 'bust'] : mode === 'body' ? ['bust', 'waist', 'hips'] : ['waist', 'hips'];
    const values = [first, second, third];
    return rows.filter(row => row.label && keys.every((key, index) => {
      const min = row[key + '_min'];
      const max = row[key + '_max'];
      const value = values[index];
      return typeof min === 'number' && typeof max === 'number' && Number.isFinite(min) && Number.isFinite(max) && min > 0 && min <= max && value >= min - tolerance && value <= max + tolerance;
    }));
  };
  class BagspotSizeTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.rows = JSON.parse(this.querySelector('[data-size-rows]').textContent).map(row => ({ ...row, label: decodeURIComponent(String(row.label || '').replace(/\+/g, '%20')) }));
        this.messages = JSON.parse(this.querySelector('[data-size-messages]').textContent);
      } catch { return; }
      if (typeof HTMLDialogElement === 'undefined') return;
      this.dialog = document.createElement('dialog');
      this.dialog.setAttribute('aria-label', this.querySelector('[data-chart] h2').textContent);
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'bug-dialog-close';
      close.textContent = this.messages.close;
      close.addEventListener('click', () => this.dialog.close());
      this.dialog.append(close, this.querySelector('[data-chart]'), this.querySelector('[data-finder]'));
      this.append(this.dialog);
      this.querySelector('[data-fallback]').hidden = true;
      this.querySelector('.bug-sizing-actions').hidden = false;
      this.querySelectorAll('[data-open]').forEach(button => button.addEventListener('click', () => {
        this.trigger = button;
        const finder = button.dataset.open === 'finder';
        this.querySelector('[data-chart]').hidden = finder;
        this.querySelector('[data-finder]').hidden = !finder;
        this.dialog.setAttribute('aria-label', this.querySelector(finder ? '[data-finder] h2' : '[data-chart] h2').textContent);
        if (finder) this.showKnownOptions();
        this.dialog.showModal();
      }));
      this.dialog.addEventListener('close', () => this.trigger?.focus());
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      });
      this.querySelector('[data-chart-unit]')?.addEventListener('change', event => {
        const divisor = event.target.value === 'in' ? 2.54 : 1;
        this.querySelectorAll('[data-min]').forEach(cell => {
          const min = Number(cell.dataset.min), max = Number(cell.dataset.max);
          cell.textContent = min > 0 && max >= min ? `${Math.round(min / divisor * 10) / 10}–${Math.round(max / divisor * 10) / 10}` : '—';
        });
      });
      this.setupChart();
      const knownForm = this.querySelector('[data-known-size]');
      knownForm?.addEventListener('submit', event => {
        event.preventDefault();
        const result = this.querySelector('[data-known-result]');
        result.replaceChildren();
        const value = this.normalizeSize(knownForm.elements.known_size.value);
        const options = this.sizeOptions();
        const matches = options.filter(option => this.normalizeSize(option.value) === value);
        if (!value) { result.textContent = this.messages.known_invalid; return; }
        if (matches.length !== 1) {
          result.textContent = !options.length ? this.messages.known_none : /^(?:\d{2,3}[A-Z]{0,3}|[A-Z]{1,12})$/.test(value) ? this.messages.known_missing : this.messages.known_invalid;
          return;
        }
        const match = matches[0];
        result.textContent = `${this.messages.known_available} ${match.value}`;
        const note = document.createElement('p');
        note.textContent = /^(?:ONESIZE|FREESIZE|OS)$/.test(value) ? this.messages.known_only : this.messages.known_result_note;
        result.append(note);
        this.offerSize(match.value, result);
      });
      knownForm?.addEventListener('input', () => this.querySelector('[data-known-result]').replaceChildren());
      const form = this.querySelector('[data-calculator]');
      if (!form) return;
      const inputs = [...form.querySelectorAll('input[data-key]')];
      const number = value => Math.round(value * 10) / 10;
      const bounds = key => {
        const ranges = this.rows.filter(row => Number.isFinite(row[key + '_min']) && Number.isFinite(row[key + '_max']));
        return ranges.length ? [Math.min(...ranges.map(row => row[key + '_min'])), Math.max(...ranges.map(row => row[key + '_max']))] : null;
      };
      const refreshUnit = () => {
        const unit = form.elements.unit.value, scale = unit === 'in' ? 2.54 : 1;
        for (const input of inputs) {
          input.closest('label').querySelector('[data-input-unit]').textContent = unit;
          input.removeAttribute('aria-invalid');
          const range = bounds(input.dataset.key);
          if (!range) continue;
          input.placeholder = `${number(range[0] / scale)}–${number(range[1] / scale)}`;
          input.closest('label').querySelector('[data-input-range]').textContent = `${this.messages.supported} ${input.placeholder} ${unit}`;
        }
      };
      form.dataset.unit = form.elements.unit.value;
      refreshUnit();
      form.addEventListener('submit', event => {
        event.preventDefault();
        const data = new FormData(form), scale = data.get('unit') === 'in' ? 2.54 : 1;
        const first = Number(data.get('first')) * scale, second = Number(data.get('second')) * scale, third = Number(data.get('third')) * scale;
        const measurements = this.dataset.mode === 'body' ? [first, second, third] : [first, second];
        const result = this.querySelector('[data-result]');
        result.replaceChildren();
        if (!(measurements.every(value => Number.isFinite(value) && value > 0 && value <= 400))) {
          result.textContent = this.messages.invalid;
          form.querySelector('input').focus();
          return;
        }
        for (const input of inputs) input.removeAttribute('aria-invalid');
        const outside = inputs.filter((input, index) => {
          const range = bounds(input.dataset.key);
          return range && (measurements[index] < range[0] - 0.3 || measurements[index] > range[1] + 0.3);
        });
        if (outside.length) {
          const unit = String(data.get('unit'));
          this.addText(result, this.messages.outside_range);
          outside.forEach(input => {
            const range = bounds(input.dataset.key);
            this.addText(result, `${input.dataset.label}: ${data.get(input.name)} ${unit}. ${this.messages.supported} ${number(range[0] / scale)}–${number(range[1] / scale)} ${unit}.`);
            input.setAttribute('aria-invalid', 'true');
          });
          this.showReference(result, inputs, measurements, scale, unit);
          this.browseProductSizes(result);
          if (unit === 'cm' && inputs.every(input => {
            const range = bounds(input.dataset.key), value = Number(data.get(input.name)) * 2.54;
            return range && value >= range[0] - 0.3 && value <= range[1] + 0.3;
          })) {
            const note = document.createElement('p'); note.textContent = this.messages.check_unit; result.append(note);
            const button = document.createElement('button'); button.type = 'button'; button.textContent = this.messages.reinterpret_inches;
            button.addEventListener('click', () => { form.elements.unit.value = 'in'; form.dataset.unit = 'in'; refreshUnit(); form.requestSubmit(); });
            result.append(button);
          }
          return;
        }
        const exact = matchRanges(this.rows, this.dataset.mode, first, second, third);
        const nearby = matchRanges(this.rows, this.dataset.mode, first, second, third, 0.3);
        const matches = exact.length ? exact : nearby;
        if (!matches.length && this.dataset.mode === 'bra') {
          const bandRows = this.rows.filter(row => first >= row.underbust_min - 0.3 && first <= row.underbust_max + 0.3);
          if (bandRows.length) {
            const minBust = Math.min(...bandRows.map(row => row.bust_min)), maxBust = Math.max(...bandRows.map(row => row.bust_max));
            if (second < minBust - 0.3 || second > maxBust + 0.3) {
              const band = String(bandRows[0].label).match(/^\d+/)?.[0] || '—';
              result.textContent = `${this.messages.band_reference} ${band}. ${this.messages.cup_outside} ${this.messages.bust_label}: ${number(minBust / scale)}–${number(maxBust / scale)} ${data.get('unit')}.`;
              this.showReference(result, inputs, measurements, scale, String(data.get('unit')));
              this.browseProductSizes(result);
              return;
            }
          }
        }
        if (!matches.length) {
          result.textContent = this.messages.no_match;
          this.showReference(result, inputs, measurements, scale, String(data.get('unit')));
          this.browseProductSizes(result);
          return;
        }
        result.textContent = matches.length > 1 ? this.messages.ambiguous : '';
        if (!exact.length) this.addText(result, this.messages.boundary_note);
        if (this.dataset.general === 'true') this.addText(result, this.messages.estimate_action);
        for (const row of matches) {
          const group = document.createElement('div');
          group.className = 'bug-size-candidate';
          this.addText(group, `${this.messages.suggested} ${row.label}`);
          this.offerSize(row.label, group);
          result.append(group);
        }
        if (exact.length && !exact.some(row => this.sizeOptions().some(option => !option.disabled && this.normalizeSize(option.value) === this.normalizeSize(row.label)))) {
          const alternatives = nearby.filter(row => !exact.includes(row) && this.sizeOptions().some(option => !option.disabled && this.normalizeSize(option.value) === this.normalizeSize(row.label)));
          if (alternatives.length) {
            this.addText(result, this.messages.boundary_note);
            alternatives.forEach(row => { this.addText(result, `${this.messages.suggested} ${row.label}`); this.offerSize(row.label, result); });
          }
        }
      });
      form.elements.unit.addEventListener('change', () => {
        const unit = form.elements.unit.value, previous = form.dataset.unit || 'in';
        if (previous !== unit) for (const name of ['first', 'second', 'third']) {
          const input = form.elements[name];
          if (input?.value && Number(input.value) > 0) input.value = String(Math.round(Number(input.value) * (unit === 'in' ? 1 / 2.54 : 2.54) * 10) / 10);
        }
        form.dataset.unit = unit;
        refreshUnit();
        this.querySelector('[data-result]').replaceChildren();
      });
      form.addEventListener('input', event => { if (event.target.tagName === 'INPUT') { event.target.removeAttribute('aria-invalid'); this.querySelector('[data-result]').replaceChildren(); } });
    }
    normalizeSize(value) {
      const normalized = String(value).normalize('NFKC').trim().toUpperCase().replace(/[\s/\-]+/g, '');
      const aliases = { FREE:'FREESIZE', SMALL:'S', MEDIUM:'M', LARGE:'L', XLARGE:'XL', EXTRALARGE:'XL', XXLARGE:'XXL', XXXLARGE:'XXXL' };
      return aliases[normalized] || normalized;
    }
    picker() { return document.getElementById('variant-selects-' + this.dataset.sectionId); }
    model() {
      const picker = this.picker();
      if (!picker) return null;
      const groups = [...picker.querySelectorAll('select, fieldset')];
      const names = groups.map(group => (group.tagName === 'SELECT' ? group.name : group.querySelector('input')?.name || '').replace(/^options\[|\]$/g, '').trim());
      const sizeIndex = names.findIndex(name => ['size', 'sizes', 'bra size', 'cup size', 'sie'].includes(name.toLowerCase()));
      let variants;
      try { variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent); } catch { return null; }
      if (!Array.isArray(variants) || Number(this.dataset.variantCount) > variants.length) return null;
      return { picker, groups, names, sizeIndex, variants };
    }
    sizeOptions() {
      const model = this.model();
      if (!model || model.sizeIndex < 0) return [];
      const groups = new Map();
      model.variants.forEach(variant => {
        const raw = variant.options[model.sizeIndex];
        const key = this.normalizeSize(raw);
        if (!groups.has(key)) groups.set(key, {value:this.displaySize(raw), disabled:true});
        if (variant.available) groups.get(key).disabled = false;
      });
      return [...groups.values()];
    }
    displaySize(value) {
      const normalized = this.normalizeSize(value);
      if (normalized === 'FREESIZE') return 'Free Size';
      return /^(?:S|M|L|XL|XXL|XXXL)$/.test(normalized) ? normalized : value;
    }

    addText(container, message) {
      const p = document.createElement('p'); p.textContent = message; container.append(p);
    }
    showReference(result, inputs, measurements, scale, unit) {
      const candidates = this.rows.filter(row => row.label && inputs.every(input => {
        const min = row[input.dataset.key + '_min'], max = row[input.dataset.key + '_max'];
        return Number.isFinite(min) && Number.isFinite(max) && min > 0 && min <= max;
      })).map(row => {
        const distances = inputs.map((input,index) => Math.max(row[input.dataset.key + '_min'] - measurements[index], measurements[index] - row[input.dataset.key + '_max'], 0));
        return {row, largest: Math.max(...distances), total: distances.reduce((sum,n) => sum + n, 0)};
      }).sort((a,b) => a.largest - b.largest || a.total - b.total);
      if (!candidates.length) return;
      const best = candidates[0];
      const closest = candidates.filter(item => Math.abs(item.largest-best.largest)<0.000001 && Math.abs(item.total-best.total)<0.000001).slice(0,3);
      const box = document.createElement('div'); box.className = 'bug-chart-reference';
      this.addText(box, this.messages.reference_only);
      const format = value => String(Number(value.toFixed(2)));
      closest.forEach(({row}) => {
        this.addText(box, `${this.messages.reference_label} ${row.label}`);
        inputs.forEach((input,index) => {
          const min = row[input.dataset.key + '_min'], max = row[input.dataset.key + '_max'], value = measurements[index];
          const difference = value < min ? `${this.messages.below_by} ${format((min-value)/scale)} ${unit}` : value > max ? `${this.messages.above_by} ${format((value-max)/scale)} ${unit}` : this.messages.within_range;
          this.addText(box, `${input.dataset.label}: ${format(value/scale)} ${unit}. ${this.messages.reference_range} ${format(min/scale)}–${format(max/scale)} ${unit}; ${difference}.`);
        });
      });
      result.append(box);
    }
    browseProductSizes(container) {
      const listed = document.createElement('div'); listed.className = 'bug-product-sizes';
      this.addText(listed, this.messages.manual_options);
      const choices = document.createElement('div');
      this.sizeOptions().forEach(option => {
        const button = document.createElement('button'); button.type = 'button';
        button.textContent = option.value + ' — ' + (option.disabled ? this.messages.stock_sold : this.messages.stock_available);
        button.addEventListener('click', () => {
          choices.replaceChildren();
          this.addText(choices, this.messages.known_result_note);
          this.offerSize(option.value, choices);
        });
        listed.append(button);
      });
      const model = this.model();
      if (model && model.sizeIndex < 0) {
        this.addText(listed, this.messages.combined_options);
        this.offerVariants(model.variants, choices, model);
      } else if (!model) this.addText(listed, this.messages.choose_manual);
      listed.append(choices);
      container.append(listed);
      return listed;
    }
    setupChart() {
      this.chartResult = document.createElement('div');
      this.chartResult.className = 'bug-result';
      this.chartResult.setAttribute('role', 'status');
      this.chartResult.setAttribute('aria-live', 'polite');
      this.querySelector('[data-chart]').append(this.chartResult);
      const listed = this.browseProductSizes(this.querySelector('[data-chart]'));
      this.querySelector('[data-chart] h2').after(listed);
      this.querySelectorAll('tbody tr').forEach(row => {
        const cell = row.querySelector('th');
        const size = cell.textContent.trim();
        const button = document.createElement('button'); button.type = 'button'; button.textContent = size;
        button.className = 'bug-chart-size';
        const option = this.sizeOptions().find(o => this.normalizeSize(o.value) === this.normalizeSize(size));
        const status = !this.model() ? this.messages.choose_manual : option ? option.disabled ? this.messages.stock_sold : this.messages.stock_available : this.messages.stock_missing;
        button.setAttribute('aria-label', `${size}: ${status}`);
        const note = document.createElement('small'); note.textContent = !this.model() ? this.messages.choose_manual : option ? option.disabled ? this.messages.stock_sold : this.messages.stock_available : this.messages.chart_not_listed;
        cell.replaceChildren(button, note);
        button.addEventListener('click', () => {
          this.chartResult.replaceChildren();
          this.addText(this.chartResult, size);
          if (this.dataset.general === 'true') this.addText(this.chartResult, this.messages.estimate_action);
          this.offerSize(size, this.chartResult);
          this.chartResult.scrollIntoView({block:'nearest'});
        });
      });
    }
    offerSize(size, result) {
      const model = this.model();
      if (!model) { this.addText(result, this.messages.choose_manual); return; }
      const variants = model.variants.filter(v => this.normalizeSize(v.options[model.sizeIndex]) === this.normalizeSize(size));
      if (!variants.length) {
        this.addText(result, this.messages.stock_missing);
        // A band-only or free-size label is never treated as an exact cup/fit match.
        const band = String(size).match(/^(\d+)[A-Z]+$/i)?.[1];
        const bandOnly = band && this.sizeOptions().some(o => this.normalizeSize(o.value) === band);
        if (bandOnly) this.addText(result, this.messages.band_only);
        this.browseProductSizes(result);
        return;
      }
      this.addText(result, variants.some(v => v.available) ? this.messages.choose_combination : this.messages.stock_sold);
      this.offerVariants(variants, result, model);
    }
    offerVariants(variants, result, model) {
      const choices = document.createElement('div'); choices.className = 'bug-variant-choices';
      variants.forEach(variant => {
        const button = document.createElement('button'); button.type = 'button';
        button.textContent = variant.options.map((value,index) => `${model.names[index]}: ${index === model.sizeIndex ? this.displaySize(value) : value}`).join(' / ') + ' — ' + (variant.available ? this.messages.stock_available : this.messages.stock_sold);
        button.disabled = !variant.available;
        button.addEventListener('click', () => this.selectVariant(variant.id, result));
        choices.append(button);
      });
      result.append(choices);
    }
    showKnownOptions() {
      const options = this.sizeOptions();
      this.querySelector('[data-known-options]').textContent = options.length ? `${this.messages.known_sizes} ${options.map(option => option.value).join(', ')}` : this.messages.known_none;
    }
    selectVariant(id, result) {
      const model = this.model();
      const variant = model?.variants.find(v => String(v.id) === String(id));
      if (typeof model?.picker.updateOptions !== 'function') { result.textContent = this.messages.choose_manual; return; }
      if (!variant?.available) { result.textContent = this.messages.unavailable; return; }
      // Validate every target before changing anything; let the existing theme own price/media/cart updates.
      const targets = model.groups.map((group,index) => group.tagName === 'SELECT'
        ? [...group.options].find(option => option.value === variant.options[index])
        : [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === variant.options[index]));
      if (targets.length !== variant.options.length || targets.some(target => !target)) {
        result.textContent = this.messages.choose_manual; return;
      }
      targets.forEach((target,index) => {
        if (model.groups[index].tagName === 'SELECT') model.groups[index].value = target.value;
        else target.checked = true;
      });
      // One change event sees the complete combination, avoiding intermediate unavailable selections.
      const changed = targets.map((target,index) => model.groups[index].tagName === 'SELECT' ? model.groups[index] : target);
      changed.forEach(target => model.picker.updateSelectedSwatchValue?.({target}));
      changed[changed.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      result.textContent = this.messages.selected;
      this.dialog.close();
      model.picker.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }
  customElements.define('bagspot-size-tools', BagspotSizeTools);
})();
